import { Box, Flex, Image, Markdown, Title } from "@qws-org/ui-kit";
import type { FC } from "react";
import React from "react";

import type { Action, Pages, WelcomeOfferBannerType } from "~/core";
import { WelcomeOfferButtons } from "~/features/welcome-offer-banner/components/WelcomeOfferButtons";
import { WelcomeOfferCards } from "~/features/welcome-offer-banner/components/WelcomeOfferCards";
import type { ImageParams } from "~/shared/utils/directus/useDirectus";

export type WelcomeOfferBannerProps = {
  data: WelcomeOfferBannerType;
  getUrl: (page: Pages) => string;
  onCallAction: (action: Action) => void;
  getImageUrl: (imageId: string, params?: ImageParams) => string;
  isActionInactive: (action: Action) => boolean;
};

export const WelcomeOfferBanner: FC<WelcomeOfferBannerProps> = (props) => {
  const { data, getImageUrl } = props;

  const middleIndex = (data?.items && Math.ceil(data.items.length / 2)) ?? 0;
  const firstColumnCards = data?.items?.slice(0, middleIndex);
  const secondColumnCards = data?.items?.slice(middleIndex);

  const mobileImage = data.background?.image;
  const mobileColor = data.background?.color ?? undefined;
  const mobileWidth = data.background?.mobile_image_width ?? undefined;
  const mobileHeight = data.background?.mobile_image_height ?? undefined;

  const tabletImage = data.background?.tablet_image ?? mobileImage;
  const tabletColor = data.background?.tablet_color ?? mobileColor;
  const tabletWidth = data.background?.tablet_image_width ?? mobileWidth;
  const tabletHeight = data.background?.tablet_image_height ?? mobileHeight;

  const desktopImage = data.background?.desktop_image ?? tabletImage;
  const desktopColor = data.background?.desktop_color ?? tabletColor;
  const desktopWidth = data.background?.desktop_image_width ?? tabletWidth;
  const desktopHeight = data.background?.desktop_image_height ?? tabletHeight;

  return (
    <Flex width="100%" maxWidth={1920} justify="center" align="center">
      <Box
        width="100%"
        height={mobileHeight}
        maxWidth={768}
        bg={
          mobileImage?.id
            ? getImageUrl(mobileImage.id, {
                width: mobileWidth,
                height: mobileHeight,
                format: "webp",
                fit: "inside",
              })
            : mobileColor
        }
        md={{
          bg: tabletImage?.id
            ? getImageUrl(tabletImage.id, {
                width: tabletWidth,
                height: tabletHeight,
                format: "webp",
                fit: "inside",
              })
            : tabletColor,
          height: tabletHeight,
          maxWidth: 1024,
          p: { pt: data.background ? 0 : 86 },
        }}
        d={{
          bg: desktopImage?.id
            ? getImageUrl(desktopImage.id, {
                width: desktopWidth,
                height: desktopHeight,
                format: "webp",
                fit: "inside",
              })
            : desktopColor,
          height: desktopHeight,
          maxWidth: 1920,
        }}
        p={{ pt: data.background ? 0 : 40 }}
        className="block mx-auto"
      >
        {!data.background && (
          <>
            {data?.title && (
              <Title
                as="h2"
                size="large"
                horizontalAlign="center"
                fontWeight="mobile.bold"
                m={{ mb: 40 }}
                md={{ m: { mb: 66 } }}
                p={{ px: 24 }}
                bg="text-accent-grad"
                textTransform="uppercase"
                className="bg-clip-text text-transparent"
              >
                {data?.title}
              </Title>
            )}

            <Flex
              flexWrap="wrap"
              d={{ flexWrap: "nowrap" }}
              md={{ justify: "center", gap: 20, flexDirection: "row" }}
              align="center"
              justify="space-evenly"
              flexDirection="column"
            >
              <WelcomeOfferCards {...props} data={firstColumnCards} />
              <Flex
                justify="center"
                className="order-3 flex-grow md:flex-grow-0 lg:order-none"
                height="100%"
                m={{ mt: "auto" }}
                md={{ maxWidth: 610 }}
              >
                <Image
                  value={getImageUrl(data.image?.id ?? "", {
                    format: "webp",
                    width: 360,
                  })}
                  mdValue={getImageUrl(data.image?.id ?? "", {
                    format: "webp",
                    width: 610,
                  })}
                  dValue={getImageUrl(data.image?.id ?? "", {
                    format: "webp",
                    width: 610,
                  })}
                />
              </Flex>
              <WelcomeOfferCards
                {...props}
                m={{ mb: 83 }}
                md={{ m: { mb: 16 } }}
                data={secondColumnCards}
              />
            </Flex>
          </>
        )}

        {data.content && <Markdown display="hidden" children={data.content} />}
        {data.controls && <WelcomeOfferButtons {...props} />}
      </Box>
    </Flex>
  );
};
